import { gpx } from '@tmcw/togeojson';
import togpx from 'togpx';

/**
 * Options for exporting GeoJSON to GPX format.
 */
export interface GPXExportOptions {
  /** Creator attribute for the GPX file (default: 'GeoVera') */
  creator?: string;
  /** Function to extract a title/name from feature properties */
  featureTitle?: (properties: Record<string, unknown>) => string;
  /** Function to extract a description from feature properties */
  featureDescription?: (properties: Record<string, unknown>) => string;
  /** Function to extract a link from feature properties */
  featureLink?: (properties: Record<string, unknown>) => string;
}

/**
 * Error thrown when GPX parsing fails.
 */
export class GPXParseError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'GPXParseError';
  }
}

/**
 * Error thrown when GPX export fails.
 */
export class GPXExportError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'GPXExportError';
  }
}

/**
 * Parses a GPX XML string into a DOM Document.
 * Handles both string and ArrayBuffer inputs.
 *
 * @param data - GPX content as string or ArrayBuffer
 * @returns Parsed XML Document
 * @throws GPXParseError if the content cannot be parsed as XML
 */
function parseGPXDocument(data: string | ArrayBuffer): Document {
  let xmlString: string;

  if (typeof data === 'string') {
    xmlString = data;
  } else {
    const decoder = new TextDecoder('utf-8');
    xmlString = decoder.decode(new Uint8Array(data));
  }

  if (!xmlString || xmlString.trim().length === 0) {
    throw new GPXParseError('GPX content is empty');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, 'application/xml');

  // Check for XML parsing errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new GPXParseError(
      `Malformed GPX XML: ${parseError.textContent?.trim() || 'Unknown parse error'}`
    );
  }

  // Verify it's actually a GPX document
  const rootElement = doc.documentElement;
  if (rootElement.tagName.toLowerCase() !== 'gpx') {
    throw new GPXParseError(
      `Invalid GPX document: expected <gpx> root element, found <${rootElement.tagName}>`
    );
  }

  return doc;
}

/**
 * Converts GPX data to a GeoJSON FeatureCollection.
 *
 * Handles GPX-specific features:
 * - Waypoints (wpt) → Point features
 * - Tracks (trk) → LineString/MultiLineString features
 * - Routes (rte) → LineString features
 *
 * Preserves attributes including name, description, elevation (ele), and time.
 *
 * @param data - GPX content as a string or ArrayBuffer
 * @returns GeoJSON FeatureCollection containing all features from the GPX file
 * @throws GPXParseError if the GPX content is invalid or cannot be parsed
 */
export function toGeoJSON(data: string | ArrayBuffer): GeoJSON.FeatureCollection {
  const doc = parseGPXDocument(data);

  try {
    const featureCollection = gpx(doc);

    // Ensure we always return a valid FeatureCollection
    if (!featureCollection || featureCollection.type !== 'FeatureCollection') {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    // Filter out any features with null geometries (can happen with empty tracks/routes)
    featureCollection.features = featureCollection.features.filter(
      (feature) => feature.geometry !== null
    );

    return featureCollection as GeoJSON.FeatureCollection;
  } catch (error) {
    if (error instanceof GPXParseError) {
      throw error;
    }
    throw new GPXParseError(
      `Failed to convert GPX to GeoJSON: ${error instanceof Error ? error.message : 'Unknown error'}`,
      error
    );
  }
}

/**
 * Converts a GeoJSON FeatureCollection to a GPX XML string.
 *
 * Uses the togpx library to perform the conversion. GPX natively supports
 * Point (waypoints), LineString (tracks/routes), and MultiLineString (multi-segment tracks).
 * Polygon features are converted to their boundary LineString representation since
 * GPX does not natively support polygon geometries.
 *
 * Feature properties are preserved as GPX extensions, and name/description can be
 * extracted from properties using the options callbacks.
 *
 * @param geojson - A GeoJSON FeatureCollection to convert
 * @param options - Optional export settings (creator, title/description extractors)
 * @returns GPX XML string
 * @throws GPXExportError if the conversion fails
 */
export function fromGeoJSON(
  geojson: GeoJSON.FeatureCollection,
  options?: GPXExportOptions
): string {
  if (!geojson || geojson.type !== 'FeatureCollection') {
    throw new GPXExportError('Input must be a valid GeoJSON FeatureCollection');
  }

  // Handle empty FeatureCollection
  if (!geojson.features || geojson.features.length === 0) {
    return buildEmptyGPX(options?.creator ?? 'GeoVera');
  }

  // Pre-process features: convert polygons to their boundary representation
  const processedGeoJSON = preprocessForGPX(geojson);

  try {
    const gpxString = togpx(processedGeoJSON, {
      creator: options?.creator ?? 'GeoVera',
      featureTitle: options?.featureTitle ?? defaultFeatureTitle,
      featureDescription: options?.featureDescription ?? defaultFeatureDescription,
      featureLink: options?.featureLink,
    });

    return gpxString;
  } catch (error) {
    throw new GPXExportError(
      `Failed to convert GeoJSON to GPX: ${error instanceof Error ? error.message : 'Unknown error'}`,
      error
    );
  }
}

/**
 * Builds a minimal valid GPX XML string for an empty FeatureCollection.
 */
function buildEmptyGPX(creator: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>\n<gpx xmlns="http://www.topografix.com/GPX/1/1" version="1.1" creator="${creator}">\n</gpx>`;
}

/**
 * Pre-processes a GeoJSON FeatureCollection for GPX export.
 * Converts Polygon/MultiPolygon features to LineString/MultiLineString
 * since GPX does not natively support polygon geometries.
 */
function preprocessForGPX(geojson: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
  const processedFeatures = geojson.features
    .filter((feature) => feature.geometry !== null)
    .map((feature) => {
      const geometry = feature.geometry;

      if (geometry.type === 'Polygon') {
        // Convert polygon to its exterior ring as a LineString
        return {
          ...feature,
          geometry: {
            type: 'LineString' as const,
            coordinates: geometry.coordinates[0],
          },
        };
      }

      if (geometry.type === 'MultiPolygon') {
        // Convert multi-polygon to MultiLineString of exterior rings
        return {
          ...feature,
          geometry: {
            type: 'MultiLineString' as const,
            coordinates: geometry.coordinates.map((polygon) => polygon[0]),
          },
        };
      }

      return feature;
    });

  return {
    type: 'FeatureCollection',
    features: processedFeatures,
  };
}

/**
 * Default function to extract a title from feature properties.
 * Looks for common name-like properties.
 */
function defaultFeatureTitle(properties: Record<string, unknown>): string {
  if (!properties) return '';
  const nameFields = ['name', 'title', 'Name', 'Title', 'NAME', 'TITLE'];
  for (const field of nameFields) {
    if (properties[field] && typeof properties[field] === 'string') {
      return properties[field] as string;
    }
  }
  return '';
}

/**
 * Default function to extract a description from feature properties.
 * Looks for common description-like properties.
 */
function defaultFeatureDescription(properties: Record<string, unknown>): string {
  if (!properties) return '';
  const descFields = ['description', 'desc', 'Description', 'Desc', 'DESCRIPTION', 'DESC'];
  for (const field of descFields) {
    if (properties[field] && typeof properties[field] === 'string') {
      return properties[field] as string;
    }
  }
  return '';
}
